import { z } from "zod";

import { clientOfferTermsReadSchema, clientOfferTermsSchema } from "./client-offer-terms.js";
import { depositReadSchema, depositSchema } from "./deposit.js";
import { dealerDossierReadSchema, dealerDossierSchema } from "./dealer-dossiers-answer.js";

/**
 * `DEALER-V2-B5b-CONTRACT`: **the dealer's dossier detail and the console's read of the dealer-access facts, the answers.** Contract only: no door answers them yet (the door PR).
 *
 * **The doors (rulings of 1 November 2026).**
 *  - `GET /api/v1/me/dealership/dossiers/{id}`, right `gererInventaire`, the dealer's own concession only: another concession's dossier answers exactly what an unknown id answers, a 404.
 *  - `GET /api/v1/dossiers/{id}/dealer-access-facts`, right `voirEtRelancerDossiers` (the console's per-dossier reads), read only, newest first.
 *
 * **One definition, two readings** (`DECISION-ANSWER-LENIENT`'s pattern): `z.strictObject` is the contract (stubs, the OpenAPI parity spec), `z.looseObject` the READ twin a client parses the live answer with.
 *
 * **The offer is what the CLIENT sees of the retained offer, from the same source, never more** (`me-dossiers.controller.ts`, `retainedOffer`). The client's view serves no duration, no nominal rate and no monthly
 * payment, so none is declared here; the prototype's four-line card is a product question raised after 8 November.
 *
 * **« Ce que vous recevrez »** serves the settlement's figures, the RULED sentence and the three APPROVED labels (D-1102-2: « Votre part », « Payé par », « Quand »), composed on the server. No rate is served: a
 * rate, when shown, comes only from the served commission, never from a literal.
 *
 * **The offer's terms (D-1102-3, « Both see all now »).** `offer.terms` is the client's own `retainedOffer.terms` (`clientOfferTermsSchema`), passed through from `lending`'s one composition: Montant financé (`terms.amountFinancedAmount`, the ONLY
 * meaning of that word), Durée, Taux nominal, Mensualité; the dealer never sees more than the client, and the bank's internal terms stay hidden. The card prints exactly those four rows. `offer.amountVehicle` stays served (additive) with
 * its own label « Montant financé pour le véhicule » (PROPOSED, the record's console words, `catalogue.ts:1435`; supersedes « Part du véhicule financée »), so no two figures of one answer carry the same label.
 */

/** The ruled sentence of « Ce que vous recevrez » (dealer's side). */
export const DEALER_RECEIPT_SENTENCE =
  "Hakili ne reçoit que sa commission : le payeur vous verse directement votre part, et la commission de Hakili par un virement séparé." as const;

/** The approved labels of « Ce que vous recevrez » (D-1102-2). */
export const DEALER_RECEIPT_LABELS = { share: "Votre part", payer: "Payé par", when: "Quand" } as const;
/** The offer card's labels served today (D-1102-3); the other three arrive with the client's own. */
export const DEALER_OFFER_LABELS = { amountVehicle: "Montant financé pour le véhicule" } as const;

/**
 * `DEALER-CLOSURE-FIELD` (A to H, 2 November 2026, PROPOSED words): **the sentence the dealer reads on a dossier closed « sans suite »**, one per case. `client` is the client's own renunciation (the marketplace's and the assisted withdrawal, one act, D-1031-15),
 * `breach` the dealer's own breach (the sixth closure code, written by the breach consumer), `hakili` any other closure by Hakili. The staff's motive and the closure code are NEVER served to the dealer: the code stays server-side, used only to derive the
 * origin and the sentence.
 */
export const DEALER_CLOSURE_WORDS = {
  client: "Le client a renoncé à ce dossier : il est clos.",
  breach: "Ce dossier a été clos par Hakili : un manquement de la concession a été constaté.",
  hakili: "Ce dossier a été clos sans suite par Hakili.",
} as const;

export const DEALER_CLOSURE_ORIGINS = ["client", "hakili"] as const;

export const DEALER_ACCESS_FACT_KINDS = ["read", "piece"] as const;

const buildDealerDossierDetail = (
  object: typeof z.strictObject,
  row: typeof dealerDossierSchema,
  terms: typeof clientOfferTermsSchema,
  lenient: boolean,
  deposit: typeof depositSchema,
) => {
  /** One fact, as the administrator's section and the console's list serve it. */
  const accessFact = object({
    id: z.string().min(1),
    kind: z.enum(DEALER_ACCESS_FACT_KINDS),
    /** The employee: id and the served display name (`displayName`). */
    actor: object({ id: z.string().min(1), displayName: z.string().min(1) }),
    /** The pièce added (code and short label); `null` for a read fact. */
    piece: object({ code: z.string().min(1), shortLabel: z.string().min(1) }).nullable(),
    /** ISO instant. */
    occurredAt: z.string(),
    /** « Identité et situation consultées par {employé}, le {date} à {heure}. » or « Pièce ajoutée par {employé} : {pièce}, le {date} à {heure}. » */
    text: z.string().min(1),
  });

  /** The retained offer as the client sees it: `null` while no offer is retained. */
  const offer = object({
    labels: object({ amountVehicle: z.literal(DEALER_OFFER_LABELS.amountVehicle) }),
    /** `DEALER-OFFER-TERMS` (D-1102-3, « Both see all now »): Montant financé, Durée, Taux nominal, Mensualité, passed through from the SAME `lending` composition as the client's `retainedOffer.terms`, byte for byte; never more than the client sees. « Montant financé » means ONLY `terms.amountFinancedAmount`. */
    terms,
    bankName: z.string().min(1),
    /** The vehicle part of the financing, francs; labelled « Montant financé pour le véhicule » (never « Montant financé », which is `terms.amountFinancedAmount`). */
    amountVehicle: z.number().int().min(0),
    feeBankAmount: z.number().int().min(0),
    feeHakiliAmount: z.number().int().min(0),
    financesBankFee: z.boolean(),
    financesHakiliFee: z.boolean(),
    /** `DEPOSIT-D`: the requested and the offered deposit, the same object as the row's `deposit` (one composer); `null` where the price is unknown. */
    deposit: deposit.nullable(),
  });

  /** « Ce que vous recevrez »: the dealership's settlement on this dossier; `null` while none is open. */
  const receipt = object({
    labels: object({
      share: z.literal(DEALER_RECEIPT_LABELS.share),
      payer: z.literal(DEALER_RECEIPT_LABELS.payer),
      when: z.literal(DEALER_RECEIPT_LABELS.when),
    }),
    /** The dealership's share, francs (`SettlementView.dealerAmount`). */
    dealerAmount: z.number().int().min(0),
    payerKind: z.string().min(1),
    status: z.enum(["attenteVirements", "voletHakiliConfirme", "voletConcessionnaireConfirme", "complet"]),
    /** The day the dealership said it received the money, `AAAA-MM-JJ`; `null` until then. */
    receivedOn: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/u)
      .nullable(),
    /** ISO instant of the dealership's confirmation; `null` until then. */
    confirmedAt: z.string().nullable(),
    /** The sentence the server renders beside a blocked control; `null` once complete. */
    blockedReason: z.string().nullable(),
    /** The ruled sentence, {@link DEALER_RECEIPT_SENTENCE}. */
    sentence: z.literal(DEALER_RECEIPT_SENTENCE),
  });

  /**
   * `B2b2` (`P1-DOS-091`, D-1031-15): **the client's consent, as the dealership may read it: a state and its instant, never the code, the texts or the amounts.**
   * `donne` (the instant it was given) or `retire` (the instant it was withdrawn); the words are composed on the server (PROPOSED, owed by A-H). The key is ABSENT where no
   * consent was ever given on the dossier (a client's own request has none; an assisted one before his word has none yet): absent, never `null`, as `accessFacts` is.
   */
  const consent = object({
    state: z.enum(["donne", "retire"]),
    /** ISO instant. */
    on: z.string().min(1),
    /** « Accord du client donné le {date} » or « Accord du client retiré le {date} », composed by the server. */
    words: z.string().min(1),
  });

  /**
   * `B2d-1` (`P1-DOS-094`): **who started the dossier**, read from `dossier.dossiers.origin` (the same two codes and the same words as the list's `origin`).
   * `awaitingConsent` is present ONLY on an assisted dossier (`origin.code = concession`) while NO consent row exists: its `words` is « En attente de l'accord du client ». Absent otherwise, never `null`.
   */
  const originSchema = object({ code: z.enum(["client", "concession"]), words: z.string().min(1) });
  const awaitingConsent = object({ words: z.string().min(1) });

  /** The closure « sans suite », `null` for an open dossier and for one that finished or was refused (their words are the existing served ones). NO code and NO motive key: the parity spec pins it. */
  const closureSchema = object({
    origin: z.enum(DEALER_CLOSURE_ORIGINS),
    /** ISO instant the dossier was closed. */
    at: z.string(),
    words: z.enum([DEALER_CLOSURE_WORDS.client, DEALER_CLOSURE_WORDS.breach, DEALER_CLOSURE_WORDS.hakili]),
  }).nullable();

  const detail = object({
    /** The server's instant, once per answer, the same name, type and source as the list's and the inventory's. */
    asOf: z.string(),
    dossier: row,
    offer: offer.nullable(),
    receipt: receipt.nullable(),
    /** The hold sentence for the dealer (B2a's ruling); `null` until that ruling exists. */
    holdSentence: z.string().min(1).nullable(),
    /** `DEALER-CLOSURE-FIELD`: optional on the lenient read twin only, so a client reading an older answer does not break. */
    closure: lenient ? closureSchema.optional() : closureSchema,
    /** The read and pièce facts, newest first: served to the dealership's administrator ONLY; the key is ABSENT for anyone else. */
    accessFacts: z.array(accessFact).optional(),
    /** `B2b2`: the client's consent state, absent while none was given. */
    consent: consent.optional(),
    /** `B2d-1`: who started the dossier. Optional on both twins so an older answer still parses. */
    origin: originSchema.optional(),
    /** `B2d-1`: the dealer's « waiting » line, assisted dossiers with no consent only; absent otherwise. */
    awaitingConsent: awaitingConsent.optional(),
  });

  const consoleFacts = object({
    asOf: z.string(),
    dossierId: z.string().min(1),
    /** Newest first. */
    facts: z.array(accessFact),
  });

  return { detail, consoleFacts };
};

const strict = buildDealerDossierDetail(
  z.strictObject,
  dealerDossierSchema,
  clientOfferTermsSchema,
  false,
  depositSchema,
);
const loose = buildDealerDossierDetail(
  z.looseObject,
  dealerDossierReadSchema,
  clientOfferTermsReadSchema,
  true,
  depositReadSchema,
);

/** `GET /api/v1/me/dealership/dossiers/{id}`, the 200 answer. Strict: an unknown key is a mismatch. */
export const dealerDossierDetailAnswerSchema = strict.detail;
/** The dealer client's reading of the same answer: unknown keys tolerated at every level. */
export const dealerDossierDetailAnswerReadSchema = loose.detail;
/** `GET /api/v1/dossiers/{id}/dealer-access-facts`, the 200 answer. */
export const dealerAccessFactsAnswerSchema = strict.consoleFacts;
/** The console's reading of the same answer. */
export const dealerAccessFactsAnswerReadSchema = loose.consoleFacts;

export type DealerDossierDetailAnswer = z.infer<typeof dealerDossierDetailAnswerSchema>;
export type DealerAccessFactsAnswer = z.infer<typeof dealerAccessFactsAnswerSchema>;
export type DealerAccessFact = DealerAccessFactsAnswer["facts"][number];
